import React, { useCallback, useEffect, useState } from 'react';
import { IConversationEnriched } from '@eagerdog/interfaces';
import { useMessages } from '../../context/MessagesContext';
import { messageService } from '../../services/message.service';
import ConversationList from './ConversationList';
import ConversationPanel from './ConversationPanel';
import styles from './Messages.module.scss';

const Messages: React.FC = () => {
  const {
    isOpen,
    closeMessages,
    activeConversationId,
    openConversation,
    pendingRecipientDogId,
    pendingRecipientIsUserId,
    pendingRecipientName,
    conversations,
    setConversations,
  } = useMessages();

  const [activeConversation, setActiveConversation] = useState<IConversationEnriched | null>(null);
  const [loadingConversations, setLoadingConversations] = useState(false);
  // Mobile: true = showing thread panel, false = showing conversation list
  const [mobileShowThread, setMobileShowThread] = useState(false);

  // Close on Escape key
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') closeMessages();
  }, [closeMessages]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleKeyDown]);

  // Load conversation list when panel opens. After loading, resolve any
  // pending activeConversationId (e.g. from an email deep-link) that couldn't
  // be matched while the list was still empty.
  useEffect(() => {
    if (!isOpen) return;
    setLoadingConversations(true);
    messageService
      .getConversations()
      .then((convs) => {
        setConversations(convs);
        // Deep-link case: activeConversationId was set before list loaded
        if (activeConversationId) {
          const found = convs.find((c) => c._id === activeConversationId) ?? null;
          if (found) {
            setActiveConversation(found);
            setMobileShowThread(true);
          }
        }
      })
      .catch(() => {})
      .finally(() => setLoadingConversations(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, setConversations]); // intentionally omit activeConversationId

  // Sync activeConversation only when the selected conversation ID changes.
  // Deliberately NOT including `conversations` in deps — updates to unread_count etc.
  // are handled inline and should not re-trigger message loading in the panel.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!activeConversationId) {
      setActiveConversation(null);
      return;
    }
    const found = conversations.find((c) => c._id === activeConversationId) ?? null;
    if (found) setActiveConversation(found);
  }, [activeConversationId]); // intentionally omit `conversations`

  // Reset mobile thread view when panel closes
  useEffect(() => {
    if (!isOpen) setMobileShowThread(false);
  }, [isOpen]);

  const handleSelectConversation = (conv: IConversationEnriched) => {
    setActiveConversation(conv);
    openConversation(conv._id);
    setMobileShowThread(true);
  };

  const handleConversationCreated = (conv: IConversationEnriched) => {
    setConversations((prev) => {
      const exists = prev.find((c) => c._id === conv._id);
      if (exists) return prev;
      return [conv, ...prev];
    });
    setActiveConversation(conv);
    openConversation(conv._id);
    setMobileShowThread(true);
  };

  const handleMobileBack = () => {
    setMobileShowThread(false);
    setActiveConversation(null);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className={styles.messagesOverlay} onClick={closeMessages} />
      <div className={styles.messagesPanel} onClick={(e) => e.stopPropagation()}>
        <div className={styles.panelHeader}>
          {mobileShowThread ? (
            <button className={styles.backBtn} onClick={handleMobileBack} aria-label="Back to conversations">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>Back</span>
            </button>
          ) : (
            <h2>Messages</h2>
          )}
          <button className={styles.closeBtn} onClick={closeMessages} aria-label="Close messages">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        <div className={`${styles.panelBody} ${mobileShowThread ? styles.mobileThread : ''}`}>
          <ConversationList
            conversations={conversations}
            activeId={activeConversation?._id ?? null}
            onSelect={handleSelectConversation}
          />
          <ConversationPanel
            conversation={activeConversation}
            pendingDogId={pendingRecipientDogId}
            pendingRecipientIsUserId={pendingRecipientIsUserId}
            pendingRecipientName={pendingRecipientName}
            onConversationCreated={handleConversationCreated}
          />
        </div>
      </div>
    </>
  );
};

export default Messages;
